import trackedNavigate from '../services/trackedNavigate';
import URLUtil from './URLUtil';
import queryString from 'query-string';

export default class RoutingUtil {
  static switchToAccount = (accountId) => {
    if (!accountId) return;

    const newPath = this.getAccountPrefixedPath("", accountId);
    trackedNavigate(newPath, false, { accountId });
  }

  static switchToSite = (siteId, accountId) => {
    if (!siteId || !accountId) return;

    const newPath = this.getAccountPrefixedPath(`/sites/${siteId}`, accountId)
    trackedNavigate(newPath, false, { siteId });
  }

  static switchToAgency = (agencyId) => {
    if (!agencyId) return;

    const newPath = this.getAgencyPrefixedPath("/agency", agencyId);
    trackedNavigate(newPath, false, { agencyId });
  }

  static switchToGroup = (groupId, accountId) => {
    if (!groupId || !accountId) return;

    const newPath = this.getAccountPrefixedPath(`/groups/${groupId}`, accountId)
    trackedNavigate(newPath, false, { groupId });
  }

  /**
   * Prefixes a path with account/agency ID from the current route.
   * Extracts IDs from the current pathname instead of session storage.
   *
   * @param {string} path - The path to prefix
   * @returns {string} - The prefixed path
   */
  static getPrefixedPath = (path) => {
    if (!path) return "";

    // Handle integer paths such as -1 for popping the history stack
    if (Number.isInteger(path)) return path;

    // Extract IDs from current pathname (route-based, not session-based)
    const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const { accountId, agencyId } = this.extractIdsFromPath(currentPathname);

    return agencyId
      ? this.getAgencyPrefixedPath(path, agencyId)
      : this.getAccountPrefixedPath(path, accountId) || "";
  }


  static getAccountPrefixedPath = (originalPath = '', accountId) => {
    if (!accountId || Number.isInteger(originalPath)) return originalPath;

    if (originalPath === '') return `/a/${accountId}`;

    const pathMappings = {
      '/account': `/a/${accountId}/settings`,
      '/settings': `/a/${accountId}/settings`,
      '/dashboards': `/a/${accountId}/dashboards`,
      '/uptime': `/a/${accountId}/uptime`,
      '/groups': `/a/${accountId}/groups`,
      '/sites': `/a/${accountId}/sites`,
      '/tasks': `/a/${accountId}/tasks`,
      '/access': `/a/${accountId}/access`
    };
    const oldPath = Object.keys(pathMappings).find((key) => originalPath.startsWith(key));
    return oldPath ? originalPath.replace(oldPath, pathMappings[oldPath]) : originalPath;
  }

  static getFullUrlWithAccount = (originalPath = '', accountId) => {
    return this.prependWithAppUrl(this.getAccountPrefixedPath(originalPath, accountId));
  }

  static getAgencyPrefixedPath = (originalPath = '', agencyId) => {
    if (!agencyId || Number.isInteger(originalPath)) return originalPath;

    if (originalPath === '/agency' || originalPath === '') return `/agency/${agencyId}`;

    const pathMappings = {
      '/agency/users': `/agency/${agencyId}/users`,
      '/agency/accounts': `/agency/${agencyId}/accounts`,
      '/agency/policies': `/agency/${agencyId}/policies`,
      '/agency/reports': `/agency/${agencyId}/reports`,
    };
    const oldPath = Object.keys(pathMappings).find((key) => originalPath.startsWith(key));
    return oldPath ? originalPath.replace(oldPath, pathMappings[oldPath]) : originalPath;
  }

  static getFullUrlWithAgency = (originalPath = '', agencyId) => {
    return this.prependWithAppUrl(this.getAgencyPrefixedPath(originalPath, agencyId));
  }

  static extractIdsFromPath = (pathname) => ({
    accountId: URLUtil.extractIdAfterSubdirectory("a", pathname),
    siteId: URLUtil.extractIdAfterSubdirectory("sites", pathname),
    groupId: URLUtil.extractIdAfterSubdirectory("groups", pathname),
    agencyId: URLUtil.extractIdAfterSubdirectory("agency", pathname)
  });

  static prependWithAppUrl = (path) => {
    const loc = document.location;
    const { protocol, host } = loc;
    const newPath = path.startsWith('/') ? path : `/${path}`;
    return `${protocol}//${host}${newPath}`;
  };

  /**
   * Check if the current URL is using a share token
   * @returns {boolean} True if share_token is present in query params
   */
  static usingShareToken = () => {
    if (typeof window === 'undefined') return false;
    const params = queryString.parse(document.location.search);
    return !!params["share_token"];
  };
};
